<template>
  <basic-deal
    ref="basicDeal"
    @form-completed="onComplete"
    @transaction-type-changed="onTransactionTypeChanged"
    @deal-type-changed="onDealTypeChanged"
    @form-changed="onChange"
    :model="formData"
    :mapper="mapper"
    :objectHandler="sidebarObjectHandler"
    :arrayHandler="sidebarArrayHandler"
  >
    <slot v-if="displayRepairs">
      <tab-content
        title="Property Details"
        :before-change="validateProperty"
        @keyup.enter.native="nextTab"
      >
        <property-section
          :basicDeal="formData"
          ref="propertySection"
        >
        </property-section>
      </tab-content>
      <tab-content
        :title="transactionTabTitle"
        :before-change="validateTransactionTabInformation"
        @keyup.enter.native="nextTab"
      >
        <purchase-section
          ref="purchaseInformation"
          v-if="isPurchase"
          :form="formData.purchase"
        />
        <refinance-section v-if="isRefinance" ref="refinanceInformation"
          :form="formData.refinance"
        />
      </tab-content>
      <tab-content
        title="Property Repairs"
        :before-change="validateRepairTab"
        @keyup.enter.native="nextTab"
      >
        <repair-section ref="repairInformation" :dealData="fullData"/>
      </tab-content>
      <tab-content title="Entity Info"
        :before-change="validateEntityTabInformation"
      >
        <entity-section ref="entityInfo" :form="formData.entity" />
      </tab-content>
    </slot>
    <slot v-else>
      <tab-content
        title="Property Details"
        :before-change="validateProperty"
        @keyup.enter.native="nextTab"
      >
        <property-section
          :basicDeal="formData"
          ref="propertySection"
        >
        </property-section>
      </tab-content>
      <tab-content
        :title="transactionTabTitle"
        :before-change="validateTransactionTabInformation"
        @keyup.enter.native="nextTab"
      >
        <purchase-section v-if="isPurchase" ref="purchaseInformation"
          :form="formData.purchase"
        />
        <refinance-section v-if="isRefinance" ref="refinanceInformation"
          :form="formData.refinance"
        />
      </tab-content>
      <tab-content title="Entity Info"
        :before-change="validateEntityTabInformation"
      >
        <entity-section ref="entityInfo" :form="formData.entity" />
      </tab-content>
    </slot>
  </basic-deal>
</template>


<script>
import { DEFINE_MISSING_INDICATORS } from '@/vuex/modules/missing/types/actions';

import { TabContent } from 'vue-form-wizard';
import BasicDeal from '@/components/new_deal/components/BasicDeal';
import PropertySection from '@/components/new_deal/components/property/PropertySection';
import PurchaseSection from '@/components/new_deal/components/purchase/PurchaseSection';
import RefinanceSection from '@/components/new_deal/components/refinance/RefinanceSection';
import RepairSection from '@/components/new_deal/components/repairs/RepairSection';
import EntitySection from '@/components/new_deal/components/entities/EntitySection';

import jshelper from '@/libs/helper';
import sidebarHelper from '@/components/base/sidebar/resources/helper';
import dealHelper from '@/components/new_deal/libs/helper';
import formDataTemplate from '../resources/formData.json';
import formDataTitleMapper from '../resources/titleMapper.json';
import generalMapper from '../resources/generalMapper.json';


export default {
  components: {
    TabContent,
    BasicDeal,
    PropertySection,
    PurchaseSection,
    RefinanceSection,
    RepairSection,
    EntitySection,
  },
  data() {
    return {
      title: 'Let\'s build your deal',
      isMounted: false,
      formData: jshelper.copy(formDataTemplate),
      mapper: formDataTitleMapper,
    };
  },
  mounted() {
    this.isMounted = true;
  },
  watch: {
    displayRepairs(newVal) {
      this.mapper.propertyRepair.hide = !newVal;
    },
  },
  computed: {
    transactionType() {
      if (!this.isMounted) { return null; }
      return this.$refs.basicDeal.formData.transactionType.name;
    },
    isPurchase() {
      return this.transactionType === 'purchase';
    },
    isRefinance() {
      return this.transactionType === 'refinance';
    },
    transactionTabTitle() {
      const defaultTitle = 'Transaction Information';
      if (!this.isMounted) { return defaultTitle; }

      if (this.isPurchase) {
        return 'Purchase Information';
      } else if (this.isRefinance) {
        return 'Refinance Information';
      }

      return defaultTitle;
    },
    displayRepairs() {
      return dealHelper.displayRepairs(this.fullData);
    },
    fullData() {
      if (this.isMounted && this.$refs.basicDeal) {
        return this.$refs.basicDeal.fullFormData;
      }
      return {};
    },
  },
  methods: {
    nextTab() {
      this.$refs.basicDeal.enterKeyupHandler();
    },
    sidebarVisitHandler(obj) {
      const visitMapper = generalMapper.property.visit;
      const objType = obj.type;
      const currType = obj[visitMapper.types[objType]];

      let htmlData = '';
      if (currType) {
        htmlData = sidebarHelper.createHtmlItems(currType, visitMapper.values[objType]);
      }

      const title = jshelper.capitalizeWord(visitMapper.titles[objType]);
      return `${title}<br/>${sidebarHelper.encapsulateHTML(htmlData)}`;
    },
    sidebarPermitHandler(obj) {
      const value = obj.exists ? 'Yes' : 'No';
      let html = '';

      if (obj.explanation) {
        html = sidebarHelper.encapsulateHTML(
          sidebarHelper.createHtmlItems(
            { explanation: obj.explanation },
            generalMapper.repairs.permits,
          ),
        );
      }

      return `${value}<br/>${html}`;
    },
    sidebarObjectHandler(obj) {
      if (jshelper.checkProp(obj, 'address')) {
        return sidebarHelper.addressHandler(obj, generalMapper.property.address, true);
      }

      if (jshelper.checkProp(obj, 'exists') && jshelper.checkProp(obj, 'explanation')) {
        return this.sidebarPermitHandler(obj);
      }

      if (jshelper.checkProp(obj, 'type')) {
        return this.sidebarVisitHandler(obj);
      }

      return obj.name || '';
    },
    sidebarArrayHandler(array) {
      let htmlData = '';
      array.forEach((obj, idx) => {
        if (obj.price) {
          htmlData += `
            <strong>Price:</strong> $ ${obj.price}
            <br/><strong>Payoff: </strong>${obj.payoff ? 'Yes' : 'No'}
          `;
        }
        if (obj.apn) {
          htmlData += `
            ${idx === 0 ? '' : '<br/>'}
            <div><strong>Address:</strong> ${obj.address || '-'}
            <br/><strong>City: </strong>${obj.city || '-'}
            <br/><strong>APN: </strong>${obj.apn}</div>
          `;
        }
      });

      return `<br/>${sidebarHelper.encapsulateHTML(htmlData)}`;
    },
    validateProperty() {
      return this.$refs.propertySection.validate();
    },
    validateTransactionTabInformation() {
      if (this.isPurchase) {
        return this.$refs.purchaseInformation.$refs.purchaseForm.validate();
      } else if (this.isRefinance) {
        return this.$refs.refinanceInformation.$refs.refinanceForm.validate();
      }

      return [];
    },
    validateRepairTab() {
      return this.$refs.repairInformation.$refs.repairForm.validate();
    },
    validateEntityTabInformation() {
      return this.$refs.entityInfo.$refs.entityInformationForm.validate();
    },
    onDealTypeChanged() {
      this.formData.propertyRepair = this.getNewFormData().propertyRepair;
    },
    onTransactionTypeChanged(transactionType) {
      this.formData.propertyRepair = this.getNewFormData().propertyRepair;

      if (transactionType === 'purchase') {
        this.mapper.purchase.hide = false;
        this.mapper.refinance.hide = true;
        this.mapper.transaction.hide = true;
        jshelper.clear(this.formData.refinance);
      } else if (transactionType === 'refinance') {
        this.mapper.purchase.hide = true;
        this.mapper.refinance.hide = false;
        this.mapper.transaction.hide = true;
        jshelper.clear(this.formData.purchase);
      } else {
        this.mapper.purchase.hide = false;
        this.mapper.refinance.hide = false;
        this.mapper.transaction.hide = true;
        jshelper.clear(this.formData.purchase);
        jshelper.clear(this.formData.refinance);
      }
    },
    getNewFormData() {
      return jshelper.copy(formDataTemplate);
    },
    createDealForSubmission() {
      const deal = jshelper.copy(this.fullData);
      delete deal.transaction;
      deal.entity = deal.entity.entity.id;

      if (this.isPurchase) {
        delete deal.refinance;
      } else if (this.isRefinance) {
        delete deal.purchase;
      }

      if (!this.displayRepairs) {
        delete deal.propertyRepair;
      }

      const crosses = deal.property.crosses;
      delete deal.property.crosses;
      if (Array.isArray(crosses) && crosses.length > 0) {
        deal.crosses = {
          additionalProperties: true,
          properties: crosses,
        };
      }

      delete deal.property.contact.name;
      delete deal.property.contact.phone;
      delete deal.property.address.lotSize;
      delete deal.property.address.building_sqft;
      delete deal.property.address.bedrooms;
      delete deal.property.address.total_baths_calculated;
      if (deal.crosses != null) {
        const propertiesToDelete = ['lotSize', 'bedrooms', 'building_sqft', 'total_baths_calculated'];
        deal.crosses.properties.forEach((obj) => {
          propertiesToDelete.forEach((prop) => {
            // eslint-disable-next-line no-param-reassign
            delete obj[prop];
          });
        });
      }
      return deal;
    },
    onComplete() {
      const deal = this.createDealForSubmission();
      const fullData = this.fullData;
      this.$reqs.post(`/users/${this.$store.getters.user.id}/deals`, deal)
        .then(() => {
          if (process.env.NODE_ENV !== 'development') {
            this.$ga.event({
              eventCategory: 'CompleteNewDeal',
              eventAction: 'Completed',
            });
            if (process.env.NODE_ENV === 'production') {
              this.$analytics.fbq.event('CompleteNewDeal');
            }
          }
          this.updateIndicators();
          this.$router.push({ name: 'DealConfirmation', params: { deal: fullData } });
        })
        .catch(() => {});
    },
    updateIndicators() {
      this.$reqs.get(`/users/${this.$store.getters.user.id}/indicators`)
        .then((response) => {
          this.$store.dispatch(DEFINE_MISSING_INDICATORS, { missing: response.data });
        })
        .catch(() => {});
    },
    onChange(prevIndex, nextIndex) {
      const entityTabIndex = this.displayRepairs ? 6 : 5;
      if (nextIndex === entityTabIndex && this.formData.entity.entity === null) {
        this.$refs.entityInfo.setDefaultEntity();
      }
    },
  },
};
</script>
<style>
</style>



// WEBPACK FOOTER //
// src/components/new_deal/components/NewDeal.vue